import { useEffect, useState, Fragment } from "react";
import {
  CheckIcon,
  ChevronDownIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Listbox, Transition } from "@headlessui/react";
import "react-datepicker/dist/react-datepicker.css";
import { Impact, Role } from "../../../Models";
import useApiToken from "../../../hooks/useApiToken";
import ConfirmModal from "../../Shared/ConfirmModal";
import {
  DeleteRole,
  GetRoleImpacts,
  UpdateRole,
  UpsertRoleImpacts,
} from "../../../api/roles.api";

type RoleFlag = { name: string; values: string };

const publishingOptions = [
  {
    name: "Published",
    description: "This job posting can be viewed by anyone who has the link.",
  },
  {
    name: "Draft",
    description: "This job posting will no longer be publicly accessible.",
  },
];

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}

function mapImpactToFlag(impact: Impact): RoleFlag {
  return {
    name: impact.name,
    values: impact.value,
  };
}

function normalizeFlags(flags: RoleFlag[]) {
  return flags
    .map((flag) => ({
      name: flag.name.trim(),
      values: flag.values.trim(),
    }))
    .filter((flag) => flag.name !== "" || flag.values !== "");
}

export default function RoleSideBarEdit({
  role,
  toggleConfigModal,
}: {
  role: Role | undefined;
  toggleConfigModal: () => void;
}) {
  const [selected, setSelected] = useState(publishingOptions[0]);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);
  const [positiveFlags, setPositiveFlags] = useState<RoleFlag[]>([]);
  const [negativeFlags, setNegativeFlags] = useState<RoleFlag[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSavingFlags, setIsSavingFlags] = useState(false);

  const token = useApiToken();
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (role) {
      setSelected(role.isActive ? publishingOptions[0] : publishingOptions[1]);
    }
  }, [role]);

  useEffect(() => {
    const loadImpacts = async () => {
      if (!token || !role?.id) {
        setPositiveFlags([]);
        setNegativeFlags([]);
        return;
      }

      try {
        const impacts = await GetRoleImpacts(role.id, token);
        setPositiveFlags(
          impacts.filter((impact) => impact.isPositive).map(mapImpactToFlag)
        );
        setNegativeFlags(
          impacts.filter((impact) => !impact.isPositive).map(mapImpactToFlag)
        );
      } catch {
        setPositiveFlags([]);
        setNegativeFlags([]);
      }
    };

    loadImpacts();
  }, [role?.id, token]);

  const handleChangePublishOption = (option: {
    name: string;
    description: string;
  }) => {
    if (!token) return;
    if (option !== selected) {
      const body = [
        {
          propertyName: "IsActive",
          propertyValue: String(option === publishingOptions[0]),
        },
      ];
      if (roleId === undefined) return;
      UpdateRole(roleId, body, token);
    }
  };

  const deleteOnConfirm = async () => {
    if (role?.id === undefined || !token) return;
    await DeleteRole(role.id, token, true);
    navigate("/roles");
    setDeleteModalIsOpen(false);
  };

  const saveFlags = async () => {
    if (!token || !role?.id) return;

    const impacts: Impact[] = [
      ...normalizeFlags(positiveFlags).map((flag) => ({
        name: flag.name,
        value: flag.values,
        isPositive: true,
      })),
      ...normalizeFlags(negativeFlags).map((flag) => ({
        name: flag.name,
        value: flag.values,
        isPositive: false,
      })),
    ];

    const savedImpacts = await UpsertRoleImpacts(role.id, impacts, token);
    setPositiveFlags(
      savedImpacts.filter((impact) => impact.isPositive).map(mapImpactToFlag)
    );
    setNegativeFlags(
      savedImpacts.filter((impact) => !impact.isPositive).map(mapImpactToFlag)
    );
  };

  const handleEditModeToggle = async () => {
    if (!isEditMode) {
      setIsEditMode(true);
      return;
    }

    setIsSavingFlags(true);
    try {
      await saveFlags();
      setIsEditMode(false);
    } finally {
      setIsSavingFlags(false);
    }
  };

  return (
    <div className="px-4 pt-4">
      <span>
        <button
          type="button"
          className="inline-flex items-center gap-x-1.5 rounded-md bg-red-500 text-white px-3 py-2 text-sm font-semibol shadow-sm ring-1 ring-inset ring-red-500 hover:bg-red-600"
          onClick={() => {
            setDeleteModalIsOpen(true);
          }}
        >
          <TrashIcon
            className="-ml-0.5 h-5 w-5 text-white"
            aria-hidden="true"
          />
          Delete Role
        </button>
      </span>

      <div className="mt-3">
        <button
          type="button"
          className="inline-flex items-center gap-x-1.5 rounded-md bg-entntblue text-white px-3 py-2 text-sm font-semibol shadow-sm hover:bg-entntorange"
          onClick={toggleConfigModal}
        >
          Role Configuration
        </button>
      </div>

      <Listbox
        as="div"
        value={selected}
        onChange={setSelected}
        className="mt-3"
      >
        {({ open }) => (
          <>
            <div className="relative">
              <div className="inline-flex divide-x divide-entntblue rounded-md shadow-sm">
                <div className="inline-flex divide-x rounded-md shadow-sm">
                  <div className="inline-flex items-center gap-x-1.5 rounded-l-md min-w-[7rem] bg-entntblue px-3 py-2 text-white shadow-sm">
                    <CheckIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                    <p className="text-sm font-semibold">{selected.name}</p>
                  </div>
                  <Listbox.Button className="inline-flex items-center rounded-l-none rounded-r-md bg-entntblue p-2 hover:bg-entntorange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-entntblue focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50">
                    <ChevronDownIcon
                      className="h-5 w-5 text-white"
                      aria-hidden="true"
                    />
                  </Listbox.Button>
                </div>
              </div>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute left-0 z-20 -mr-1 mt-2 w-60 origin-top-right divide-y divide-gray-200 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:left-auto sm:right-0">
                  {publishingOptions.map((option) => (
                    <Listbox.Option
                      key={option.name}
                      className={({ active }) =>
                        classNames(
                          active
                            ? "bg-entntorange text-white"
                            : "text-gray-900",
                          "cursor-default select-none p-4 text-sm"
                        )
                      }
                      value={option}
                      onClick={() => {
                        handleChangePublishOption(option);
                      }}
                    >
                      {({ selected: isSelected, active }) => (
                        <div className="flex flex-col">
                          <div className="flex justify-between">
                            <p
                              className={
                                isSelected ? "font-semibold" : "font-normal"
                              }
                            >
                              {option.name}
                            </p>
                            {isSelected ? (
                              <span
                                className={
                                  active ? "text-white" : "text-entntblue"
                                }
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </div>
                          <p
                            className={classNames(
                              active ? "text-white" : "text-gray-500",
                              "mt-2"
                            )}
                          >
                            {option.description}
                          </p>
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>

      <div className="mt-3">
        <Link
          to={`/roles/${roleId}/prompt-config-builder`}
          className="inline-flex items-center gap-x-1.5 rounded-md bg-entntblue text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-entntorange"
        >
          Prompt Config Builder
        </Link>
      </div>

      <ConfirmModal
        open={deleteModalIsOpen}
        setOpen={setDeleteModalIsOpen}
        onConfirm={deleteOnConfirm}
        type="danger"
        title="Delete job"
        description=" Are you sure you want to delete this job? All of the
    data will be permanently removed. This action cannot
    be undone."
      />

      <div className="mt-5 border-t border-gray-200 pt-4 dark:border-gray-600">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Flags
          </h3>
          <button
            type="button"
            className="text-xs text-entntblue hover:text-entntorange dark:text-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={handleEditModeToggle}
            disabled={isSavingFlags}
          >
            {isSavingFlags ? "Saving..." : isEditMode ? "Done" : "Edit"}
          </button>
        </div>

        <div className="mb-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">
              Positive
            </span>
            {isEditMode && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-700 dark:text-green-400"
                onClick={() =>
                  setPositiveFlags((prev) => [
                    ...prev,
                    { name: "", values: "" },
                  ])
                }
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add Positive Flag
              </button>
            )}
          </div>
          {positiveFlags.length === 0 && !isEditMode && (
            <p className="text-xs italic text-gray-400 dark:text-gray-500">
              No positive flags
            </p>
          )}
          {positiveFlags.map((flag, idx) =>
            isEditMode ? (
              <div key={idx} className="mb-2 flex items-start gap-1">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <input
                    type="text"
                    placeholder="Flag name (e.g. Past Company)"
                    value={flag.name}
                    onChange={(e) => {
                      const updated = [...positiveFlags];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      setPositiveFlags(updated);
                    }}
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-entntblue dark:border-gray-600 dark:bg-darkbglight dark:text-gray-200"
                  />
                  <input
                    type="text"
                    placeholder="Values (e.g. Google, Microsoft)"
                    value={flag.values}
                    onChange={(e) => {
                      const updated = [...positiveFlags];
                      updated[idx] = { ...updated[idx], values: e.target.value };
                      setPositiveFlags(updated);
                    }}
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-entntblue dark:border-gray-600 dark:bg-darkbglight dark:text-gray-200"
                  />
                </div>
                <button
                  type="button"
                  className="mt-1 text-gray-400 hover:text-red-500"
                  onClick={() =>
                    setPositiveFlags((prev) =>
                      prev.filter((_, i) => i !== idx)
                    )
                  }
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div key={idx} className="mb-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {flag.name}: {" "}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {flag.values}
                </span>
              </div>
            )
          )}
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-red-500 dark:text-red-400">
              Negative
            </span>
            {isEditMode && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-600 dark:text-red-400"
                onClick={() =>
                  setNegativeFlags((prev) => [
                    ...prev,
                    { name: "", values: "" },
                  ])
                }
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add Negative Flag
              </button>
            )}
          </div>
          {negativeFlags.length === 0 && !isEditMode && (
            <p className="text-xs italic text-gray-400 dark:text-gray-500">
              No negative flags
            </p>
          )}
          {negativeFlags.map((flag, idx) =>
            isEditMode ? (
              <div key={idx} className="mb-2 flex items-start gap-1">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <input
                    type="text"
                    placeholder="Flag name (e.g. Past Company)"
                    value={flag.name}
                    onChange={(e) => {
                      const updated = [...negativeFlags];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      setNegativeFlags(updated);
                    }}
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-entntblue dark:border-gray-600 dark:bg-darkbglight dark:text-gray-200"
                  />
                  <input
                    type="text"
                    placeholder="Values (e.g. Google, Microsoft)"
                    value={flag.values}
                    onChange={(e) => {
                      const updated = [...negativeFlags];
                      updated[idx] = { ...updated[idx], values: e.target.value };
                      setNegativeFlags(updated);
                    }}
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-entntblue dark:border-gray-600 dark:bg-darkbglight dark:text-gray-200"
                  />
                </div>
                <button
                  type="button"
                  className="mt-1 text-gray-400 hover:text-red-500"
                  onClick={() =>
                    setNegativeFlags((prev) =>
                      prev.filter((_, i) => i !== idx)
                    )
                  }
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div key={idx} className="mb-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {flag.name}: {" "}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {flag.values}
                </span>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

